import type {Ref, MutableRefObject, SyntheticEvent} from 'react';
import type {Placement} from './typings';

/**
 * @description The utility allows to attach multiple refs to a single React element.
 * - Accepts any number of refs (functions or objects).
 * - When the element mounts, it calls each ref with the DOM node, so all refs are updated.
 * - Handles both function refs and object refs (e.g., ref={myRef} and ref={node => ...}).
 * Prevents ref overwriting and enables robust composition of reusable components.
 * @param refs array of refs in several formats
 * @returns {Function} returns a generated procedure function
 */
export const composeRefs = <T>(...refs: Array<Ref<T> | MutableRefObject<T> | undefined>) => {
  return (node: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as MutableRefObject<T>).current = node;
      }
    });
  };
};

/**
 * @description The utility combines two event handlers
 * (e.g., onMouseEnter from the child and the Tooltip's own handler) into a single handler.
 *
 * - Ensures both the child's logic and the Tooltip's logic run for events like mouse enter, mouse leave, focus, and blur.
 * - Prevents overwriting the child's event handlers when adding Tooltip behavior.
 * - Maintains correct event flow and avoids breaking the trigger element's own functionality.
 * @param childEventHandler - The event handler from the child component.
 * @param tooltipEventHandler - The event handler from the Tooltip component.
 * @returns A merged event handler that calls both handlers.
 */
export const mergeHandlers = <E extends SyntheticEvent>(
  childEventHandler: ((e: E) => void) | undefined,
  tooltipEventHandler: (e: E) => void,
) => {
  return (e: E) => {
    try {
      childEventHandler?.(e);
    } finally {
      if (!e.defaultPrevented) tooltipEventHandler(e);
    }
  };
};

/**
 * @description Calculate the coordinates of the tooltip relative to its trigger element,
 * based on the desired placement (top, bottom, left, or right),
 * the size of the tooltip, the size and position of the trigger,
 * and the viewport dimensions.
 * @param {object} params - The parameters for computing the coordinates.
 * @returns The computed coordinates for the tooltip.
 */
export const calculateCoordinates = ({
  triggerRect,
  tooltipSize,
  viewport,
  placement,
  offset,
  margin,
}: {
  triggerRect: DOMRect;
  tooltipSize: {width: number; height: number};
  viewport: {width: number; height: number};
  placement: Exclude<Placement, 'auto'>;
  offset: number;
  margin: number;
}) => {
  const {width: tw, height: th} = tooltipSize;
  const {width: vw, height: vh} = viewport;

  let x = 0;
  let y = 0;

  switch (placement) {
    case 'top':
      x = triggerRect.left + triggerRect.width / 2 - tw / 2;
      y = triggerRect.top - th - offset;
      break;
    case 'bottom':
      x = triggerRect.left + triggerRect.width / 2 - tw / 2;
      y = triggerRect.bottom + offset;
      break;
    case 'left':
      x = triggerRect.left - tw - offset;
      y = triggerRect.top + triggerRect.height / 2 - th / 2;
      break;
    case 'right':
      x = triggerRect.right + offset;
      y = triggerRect.top + triggerRect.height / 2 - th / 2;
      break;
  }

  /**
   * @description Relaxed "isFitting" logic:
   * - For top/bottom → require only vertical space check
   * - For left/right → require only horizontal space check
   * Horizontal/vertical overflow is handled later by clamping.
   */
  let isFitting = false;
  if (placement === 'top' || placement === 'bottom') {
    isFitting = y >= margin && y + th + margin <= vh;
  } else {
    isFitting = x >= margin && x + tw + margin <= vw;
  }

  /**
   * @description Clamping logic for tooltip positioning.
   * Ensures the tooltip stays within the viewport boundaries.
   * - it doesn't overflow off-screen.
   * - clamped value is positive
   */
  const maxClampedX = Math.max(vw - tw - margin, margin);
  const maxClampedY = Math.max(vh - th - margin, margin);

  const clampedX = Math.min(Math.max(x, margin), maxClampedX);
  const clampedY = Math.min(Math.max(y, margin), maxClampedY);

  return {x, y, clampedX, clampedY, isFitting};
};

/**
 * @description Get the recalculated position for the tooltip based on the trigger element and desired placement.
 * @param {object} params - The parameters for recalculating the position.
 * @returns The recalculated position for the tooltip.
 */
export const getRecalculatedPosition = ({
  offset,
  placement,
  triggerElement,
  tooltipElement,
  margin = 8,
}: {
  offset: number;
  placement: Placement;
  triggerElement: HTMLElement;
  tooltipElement: HTMLDivElement;
  margin?: number;
}) => {
  const triggerRect = triggerElement.getBoundingClientRect();

  /**
   * Measure tooltip size (without layout flicker)
   */
  const prev = tooltipElement.style.visibility;
  tooltipElement.style.visibility = 'hidden';
  tooltipElement.style.display = 'block';
  const tooltipSize = {
    width: tooltipElement.offsetWidth,
    height: tooltipElement.offsetHeight,
  };
  tooltipElement.style.visibility = prev;

  const viewport = {width: window.innerWidth, height: window.innerHeight};

  /**
   * Generate candidate placements in priority order:
   * 1. Start with requested placement
   * 2. If not possible - fall back to top, bottom, right, left
   */
  const order: Exclude<Placement, 'auto'>[] =
    placement === 'auto'
      ? ['top', 'bottom', 'right', 'left']
      : (Array.from(new Set([placement, 'top', 'bottom', 'right', 'left'])) as Exclude<
          Placement,
          'auto'
        >[]);

  /**
   * Pick best coordinates
   * Try each candidate placement until one fully isFitting
   */
  const result = order.reduce(
    (recalculated, currentPlacement) => {
      const candidateCoords = calculateCoordinates({
        triggerRect,
        tooltipSize,
        viewport,
        placement: currentPlacement,
        offset,
        margin,
      });

      if (!recalculated.chosenCoords) {
        /** First candidate: keep as fallback */
        return {chosenCoords: candidateCoords, chosenPlacement: currentPlacement};
      }
      if (candidateCoords.isFitting && !recalculated.chosenCoords.isFitting) {
        /** Found first fully fitting placement */
        return {chosenCoords: candidateCoords, chosenPlacement: currentPlacement};
      }
      return recalculated;
    },
    {
      chosenCoords: null as ReturnType<typeof calculateCoordinates> | null,
      chosenPlacement: order[0],
    },
  );

  /**
   * Handle top/bottom placement when fit fails:
   * Pick whichever side has more space.
   */
  if (
    result.chosenCoords?.isFitting === false &&
    (result.chosenPlacement === 'top' || result.chosenPlacement === 'bottom')
  ) {
    const {top, bottom} = triggerRect;
    const tooltipHeight = tooltipSize.height;
    const viewportHeight = viewport.height;

    const spaceAbove = top;
    const spaceBelow = viewportHeight - bottom;

    if (spaceBelow >= tooltipHeight + offset) {
      result.chosenCoords.y = bottom + offset;
      result.chosenPlacement = 'bottom';
    } else if (spaceAbove >= tooltipHeight + offset) {
      result.chosenCoords.y = top - tooltipHeight - offset;
      result.chosenPlacement = 'top';
    } else {
      result.chosenCoords.y = Math.min(
        Math.max(top - tooltipHeight - offset, offset),
        viewportHeight - tooltipHeight - offset,
      );
    }

    result.chosenCoords.clampedY = Math.min(
      Math.max(result.chosenCoords.y, margin),
      viewportHeight - tooltipSize.height - margin,
    );
  }

  /**
   * @description Small-screen edge case:
   * If screen is narrow AND the tooltip cannot fit horizontally (even if vertical "isFitting" is true),
   * force full-width placement above/below and set x = margin.
   */
  const isSmallScreen = viewport.width < 600;
  const isNotFitHorizontally = tooltipSize.width + margin * 2 > viewport.width;

  if (isSmallScreen && isNotFitHorizontally && result.chosenCoords) {
    const {top, bottom} = triggerRect;
    const viewportHeight = viewport.height;
    const tooltipHeight = tooltipSize.height;

    /** Force left edge to margin */
    result.chosenCoords.x = margin;
    result.chosenCoords.clampedX = margin;

    /** Choose side with more vertical room */
    const spaceAbove = top;
    const spaceBelow = viewportHeight - bottom;

    if (spaceBelow >= tooltipHeight + offset) {
      result.chosenCoords.y = bottom + offset;
      result.chosenPlacement = 'bottom';
    } else if (spaceAbove >= tooltipHeight + offset) {
      result.chosenCoords.y = top - tooltipHeight - offset;
      result.chosenPlacement = 'top';
    } else {
      /** IF Tight height → clamp */
      result.chosenCoords.y = Math.min(
        Math.max(top - tooltipHeight - offset, margin),
        viewportHeight - tooltipHeight - margin,
      );
    }

    result.chosenCoords.clampedY = Math.min(
      Math.max(result.chosenCoords.y, margin),
      viewportHeight - tooltipHeight - margin,
    );
  }

  return result;
};
